import {
  useId
} from 'react'
import {
  ChevronRight, Loader2
} from 'lucide-react'

interface TapToPickCardProps {
  name: string
  description: string
  // Absent for the "Create Your Own" card, which shows a generic silhouette.
  pictureUrl?: string | null | undefined
  actionLabel: string
  // While disabled, the selected card is the one being saved.
  selected?: boolean
  disabled: boolean
  onTap: () => void
}

function PersonaSilhouette(): JSX.Element {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      data-testid="persona-silhouette"
      className="w-full h-full text-gray-300"
    >
      <circle cx="32" cy="25" r="11" fill="currentColor" />
      <path d="M10 64c0-14 10-23 22-23s22 9 22 23z" fill="currentColor" />
    </svg>
  )
}

/**
 * One option of the tap-to-pick assistant picker (the `test` arm of the
 * mobile picker experiment): the whole card is the button, and it says so with
 * a raised surface, a press state and an action pill. The description is never
 * clamped, so a long bio wraps onto more lines instead of losing its end.
 */
export function TapToPickCard({
  name,
  description,
  pictureUrl,
  actionLabel,
  selected = false,
  disabled,
  onTap,
}: TapToPickCardProps): JSX.Element {
  const saving = selected && disabled
  const descriptionId = useId()
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={disabled}
      aria-label={name}
      aria-describedby={descriptionId}
      className={`flex flex-col items-center h-full px-3 pt-4 pb-3 rounded-2xl border-2 bg-white shadow-soft transition-[border-color,box-shadow,transform] duration-150 active:scale-[0.97] active:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 ${
        selected ? 'border-primary-500' : 'border-gray-200'
      } ${disabled ? 'cursor-wait' : 'cursor-pointer'} ${disabled && !selected ? 'opacity-60' : ''}`}
    >
      <div className="w-16 h-16 rounded-full overflow-hidden mb-2 ring-2 ring-gray-100 bg-gray-100 shrink-0">
        {pictureUrl ? (
          <img src={pictureUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <PersonaSilhouette />
        )}
      </div>
      <h3 className="font-semibold text-gray-900 text-sm text-center">{name}</h3>
      <p
        id={descriptionId}
        lang="en"
        className="text-xs text-gray-500 mt-1 mb-3 text-center leading-snug break-words hyphens-auto"
      >
        {description}
      </p>
      <span
        aria-hidden="true"
        className={`mt-auto w-full inline-flex items-center justify-center gap-1 rounded-full py-2 text-xs font-semibold transition-colors duration-150 ${
          selected ? 'bg-primary-500 text-white' : 'bg-primary-50 text-primary-700'
        }`}
      >
        {saving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <>
            {actionLabel}
            <ChevronRight className="w-3.5 h-3.5" />
          </>
        )}
      </span>
    </button>
  )
}
